import { useQuery, useMutation, useLazyQuery } from "@apollo/client";
import {
  ADMIN_LEADS,
  ADMIN_LEAD_DETAIL,
  ADMIN_LEAD_STATS,
  ADMIN_EXPORT_LEADS,
  ADMIN_CREATE_LEAD,
  ADMIN_UPDATE_LEAD,
  ADMIN_UPDATE_LEAD_STATUS,
  ADMIN_ADD_LEAD_INTERACTION,
  ADMIN_ASSIGN_LEAD,
  ADMIN_DELETE_LEAD,
  ADMIN_CONVERT_LEAD,
} from "../graphql";
import type {
  PaginatedLeadsResult,
  Lead,
  LeadStats,
  LeadFiltersInput,
  LeadStatus,
  LeadTier,
  LeadSource,
} from "../types";

// ─── Queries ─────────────────────────────────────────────────────────────────

interface UseLeadsOptions {
  search?: string;
  status?: LeadStatus[];
  tier?: LeadTier[];
  source?: LeadSource[];
  assignedTo?: string;
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  offset?: number;
}

export function useLeads(options: UseLeadsOptions = {}) {
  const { search, status, tier, source, assignedTo, dateFrom, dateTo, limit = 20, offset = 0 } = options;

  const filters: LeadFiltersInput = {};
  if (search) filters.search = search;
  if (status && status.length > 0) filters.status = status;
  if (tier && tier.length > 0) filters.tier = tier;
  if (source && source.length > 0) filters.source = source;
  if (assignedTo) filters.assignedTo = assignedTo;
  if (dateFrom) filters.dateFrom = dateFrom;
  if (dateTo) filters.dateTo = dateTo;

  return useQuery<{ adminLeads: PaginatedLeadsResult }>(ADMIN_LEADS, {
    variables: {
      filters: Object.keys(filters).length > 0 ? filters : undefined,
      pagination: { limit, offset },
    },
    fetchPolicy: "cache-and-network",
  });
}

export function useLeadDetail(leadId: string) {
  return useQuery<{ adminLeadDetail: Lead }>(ADMIN_LEAD_DETAIL, {
    variables: { id: leadId },
    skip: !leadId,
    fetchPolicy: "cache-and-network",
  });
}

export function useLeadStats(filters?: LeadFiltersInput) {
  return useQuery<{ adminLeadStats: LeadStats }>(ADMIN_LEAD_STATS, {
    variables: {
      filters: filters && Object.keys(filters).length > 0 ? filters : undefined,
    },
    fetchPolicy: "cache-and-network",
  });
}

export function useExportLeads() {
  return useLazyQuery<{ adminExportLeads: string }>(ADMIN_EXPORT_LEADS, {
    fetchPolicy: "network-only",
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function useCreateLead() {
  return useMutation(ADMIN_CREATE_LEAD, {
    refetchQueries: ["AdminLeads", "AdminLeadStats"],
  });
}

export function useUpdateLead() {
  return useMutation(ADMIN_UPDATE_LEAD, {
    refetchQueries: ["AdminLeads", "AdminLeadStats"],
  });
}

export function useUpdateLeadStatus() {
  return useMutation(ADMIN_UPDATE_LEAD_STATUS, {
    refetchQueries: ["AdminLeads", "AdminLeadStats"],
  });
}

export function useAddLeadInteraction() {
  return useMutation(ADMIN_ADD_LEAD_INTERACTION, {
    refetchQueries: ["AdminLeadDetail"],
  });
}

export function useAssignLead() {
  return useMutation(ADMIN_ASSIGN_LEAD, {
    refetchQueries: ["AdminLeads", "AdminLeadDetail"],
  });
}

export function useDeleteLead() {
  return useMutation(ADMIN_DELETE_LEAD, {
    refetchQueries: ["AdminLeads", "AdminLeadStats"],
  });
}

export function useConvertLead() {
  return useMutation(ADMIN_CONVERT_LEAD, {
    refetchQueries: ["AdminLeads", "AdminLeadStats", "AdminLeadDetail"],
  });
}
